import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, Chip, Tooltip, Tabs, Tab, Divider,
  Grid
} from "@mui/material";
import {
  CheckCircle, Cancel, HourglassEmpty, Search, Visibility,
  TrendingUp, TrendingDown, AdminPanelSettings, Refresh
} from "@mui/icons-material";
import {
  collection, query, where, getDocs, updateDoc,
  deleteDoc, doc, Timestamp, getDoc
} from "firebase/firestore";
import { db, auth } from "../../../firebase";
import Swal from "sweetalert2";

function ApprovalManager() {
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [moduleTab, setModuleTab] = useState("all"); // 'all' | 'expense' | 'income'
  const [schoolId, setSchoolId] = useState(null);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal Review States
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data()?.schoolId;
      setSchoolId(sId);

      if (sId) {
        await fetchPendingApprovals(sId);
      }
    } catch (e) {
      console.error("Initialization Error:", e);
    }
  };

  const fetchPendingApprovals = async (sId) => {
    setLoading(true);
    try {
      // 1. Fetch Pending Expenses
      const expenseQuery = query(
        collection(db, "expenses"),
        where("schoolId", "==", sId),
        where("approvalStatus", "in", ["pending_edit", "pending_delete"])
      );
      const expenseSnap = await getDocs(expenseQuery);
      const expenseList = expenseSnap.docs.map(d => ({
        id: d.id,
        module: "Expense",
        collectionName: "expenses",
        ...d.data()
      }));

      // 2. Fetch Pending Income Entries
      const incomeQuery = query(
        collection(db, "income"),
        where("schoolId", "==", sId),
        where("approvalStatus", "in", ["pending_edit", "pending_delete"])
      );
      const incomeSnap = await getDocs(incomeQuery);
      const incomeList = incomeSnap.docs.map(d => ({
        id: d.id,
        module: "Income",
        collectionName: "income",
        ...d.data()
      }));

      // Combine & sort by recent update
      const combined = [...expenseList, ...incomeList].sort((a, b) => {
        const timeA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
        const timeB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
        return timeB - timeA;
      });

      setPendingItems(combined);
    } catch (e) {
      console.error("Error fetching approvals:", e);
      Swal.fire("Error", "Failed to load pending approval requests.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Approval / Rejection Processor
  const handleApprovalDecision = async (item, decision) => {
    setActionLoading(true);
    try {
      const docRef = doc(db, item.collectionName, item.id);

      if (decision === "approve") {
        if (item.approvalStatus === "pending_delete") {
          // Process Permanent Deletion
          await deleteDoc(docRef);
          Swal.fire("Approved", `${item.module} entry has been permanently deleted.`, "success");
        } else if (item.approvalStatus === "pending_edit") {
          // Apply Pending Edits
          const updatedFields = item.pendingData || {};
          await updateDoc(docRef, {
            ...updatedFields,
            approvalStatus: "approved",
            pendingData: null,
            approvalNote: adminNote || "Edit approved by admin",
            updatedAt: Timestamp.now()
          });
          Swal.fire("Approved", `${item.module} changes applied successfully.`, "success");
        }
      } else if (decision === "reject") {
        // Reject Request and Restore Approved Status
        await updateDoc(docRef, {
          approvalStatus: "approved",
          pendingData: null,
          approvalNote: adminNote || "Request rejected by admin",
          updatedAt: Timestamp.now()
        });
        Swal.fire("Rejected", "Request has been rejected.", "info");
      }

      setReviewModalOpen(false);
      setSelectedItem(null);
      setAdminNote("");
      fetchPendingApprovals(schoolId);
    } catch (e) {
      console.error("Approval Error:", e);
      Swal.fire("Error", e.message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Filtering Logic
  const filteredItems = pendingItems.filter(item => {
    const matchesModule =
      moduleTab === "all" ? true : item.module.toLowerCase() === moduleTab;

    const searchTerm = search.toLowerCase();
    const matchesSearch =
      item.personName?.toLowerCase().includes(searchTerm) ||
      (item.expenseHead || item.incomeHead || "").toLowerCase().includes(searchTerm) ||
      item.description?.toLowerCase().includes(searchTerm) ||
      item.amount?.toString().includes(searchTerm);

    return matchesModule && matchesSearch;
  });

  const getStatusChip = (status) => {
    if (status === "pending_edit") {
      return <Chip label="Pending Edit" color="warning" size="small" icon={<HourglassEmpty sx={{ fontSize: '14px' }} />} sx={{ fontWeight: 600, fontSize: '11px' }} />;
    }
    return <Chip label="Pending Delete" color="error" size="small" icon={<HourglassEmpty sx={{ fontSize: '14px' }} />} sx={{ fontWeight: 600, fontSize: '11px' }} />;
  };

  return (
    <Box p={2} bgcolor="#f8fafc" minHeight="100vh">
      {/* Header Bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="primary.dark" sx={{ lineHeight: 1.2 }}>
            Approvals & Modifications Center
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Review, approve, or reject pending changes for Income and Expense entries
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          size="small"
          onClick={() => fetchPendingApprovals(schoolId)}
          sx={{ borderRadius: 1.5, height: 32, fontSize: '12px' }}
        >
          Refresh Queue
        </Button>
      </Stack>

      {/* Summary KPI Cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #ed6c02', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL PENDING REQUESTS</Typography>
            <Typography variant="h5" fontWeight={800} color="warning.main">{pendingItems.length}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #d32f2f', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>PENDING EXPENSE EDITS / DELETES</Typography>
            <Typography variant="h5" fontWeight={800} color="error.main">
              {pendingItems.filter(i => i.module === "Expense").length}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #2e7d32', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>PENDING INCOME EDITS / DELETES</Typography>
            <Typography variant="h5" fontWeight={800} color="success.main">
              {pendingItems.filter(i => i.module === "Income").length}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters & Tabs Section */}
      <Card sx={{ p: 1, mb: 2, borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1}>
          <Tabs
            value={moduleTab}
            onChange={(e, val) => { setModuleTab(val); setPage(0); }}
            sx={{ minHeight: 36, '.MuiTab-root': { py: 0.5, minHeight: 36, fontSize: '13px', fontWeight: 600 } }}
          >
            <Tab label={`All (${pendingItems.length})`} value="all" />
            <Tab label={`Expenses (${pendingItems.filter(i => i.module === "Expense").length})`} value="expense" />
            <Tab label={`Income (${pendingItems.filter(i => i.module === "Income").length})`} value="income" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search pending records..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              sx: { borderRadius: 1.5, height: 34, fontSize: '12px', minWidth: 260 }
            }}
          />
        </Stack>
      </Card>

      {/* Requests Data Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f1f5f9" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Sr</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Request Type</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Head / Category</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Person / Entity</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Current Amount</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Requested By</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1, fontSize: '12.5px' }}>Review</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, fontSize: '13px', color: 'text.secondary' }}>No pending approval requests found.</TableCell></TableRow>
            ) : (
              filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                const isExpense = item.module === "Expense";

                return (
                  <TableRow key={`${item.collectionName}_${item.id}`} hover>
                    <TableCell sx={{ py: 1, fontSize: '12.5px' }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        icon={isExpense ? <TrendingDown sx={{ fontSize: '14px !important' }} /> : <TrendingUp sx={{ fontSize: '14px !important' }} />}
                        label={item.module}
                        color={isExpense ? "error" : "success"}
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '11px', height: 22, fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>{getStatusChip(item.approvalStatus)}</TableCell>
                    <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '12.5px' }}>
                      {item.expenseHead || item.incomeHead || "-"}
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: '12.5px' }}>{item.personName || "-"}</TableCell>
                    <TableCell sx={{ py: 1, fontWeight: 700, fontSize: '12.5px', color: isExpense ? 'error.main' : 'success.main' }}>
                      ₹{parseFloat(item.amount || 0).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: '11.5px', color: 'text.secondary' }}>
                      {item.pendingData?.requestedBy || "User"}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title="Inspect & Respond">
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={<AdminPanelSettings sx={{ fontSize: '16px' }} />}
                          onClick={() => { setSelectedItem(item); setReviewModalOpen(true); }}
                          sx={{ fontSize: '11px', py: 0.3, px: 1.5, borderRadius: 1.5, textTransform: 'none' }}
                        >
                          Review
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ ".MuiTablePagination-toolbar": { minHeight: 36 }, ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: '12px' } }}
        />
      </TableContainer>

      {/* Inspection & Approval Modal */}
      <Dialog open={reviewModalOpen} onClose={() => setReviewModalOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>
          Review Pending {selectedItem?.module} Request
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {selectedItem && (
            <Stack spacing={2}>
              {/* Status Header Banner */}
              <Box p={1.5} borderRadius={1.5} bgcolor={selectedItem.approvalStatus === "pending_delete" ? "#fef2f2" : "#fffbe6"} border="1px solid" borderColor={selectedItem.approvalStatus === "pending_delete" ? "error.light" : "warning.light"}>
                <Typography variant="body2" fontWeight={700} color={selectedItem.approvalStatus === "pending_delete" ? "error.main" : "warning.dark"}>
                  {selectedItem.approvalStatus === "pending_delete" ? "⚠️ Permanent Deletion Requested" : "📝 Field Modification Requested"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted by: <strong>{selectedItem.pendingData?.requestedBy || "User"}</strong>
                </Typography>
              </Box>

              {/* Data Diff Comparison for Edits */}
              {selectedItem.approvalStatus === "pending_edit" && selectedItem.pendingData ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                        CURRENT RECORD
                      </Typography>
                      <Typography variant="caption" display="block"><strong>Head:</strong> {selectedItem.expenseHead || selectedItem.incomeHead || "-"}</Typography>
                      <Typography variant="caption" display="block"><strong>Person:</strong> {selectedItem.personName}</Typography>
                      <Typography variant="caption" display="block"><strong>Amount:</strong> ₹{selectedItem.amount}</Typography>
                      <Typography variant="caption" display="block"><strong>Date:</strong> {selectedItem.date}</Typography>
                      <Typography variant="caption" display="block"><strong>Pay Mode:</strong> {selectedItem.payMode}</Typography>
                      <Typography variant="caption" display="block"><strong>Daybook:</strong> {selectedItem.dayBook}</Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f0fdf4', borderColor: 'success.light', borderRadius: 1.5 }}>
                      <Typography variant="caption" fontWeight={700} color="success.dark" display="block" mb={1}>
                        PROPOSED CHANGES
                      </Typography>
                      <Typography variant="caption" display="block"><strong>Head:</strong> {selectedItem.pendingData.expenseHead || selectedItem.pendingData.incomeHead || "-"}</Typography>
                      <Typography variant="caption" display="block"><strong>Person:</strong> {selectedItem.pendingData.personName}</Typography>
                      <Typography variant="caption" display="block"><strong>Amount:</strong> ₹{selectedItem.pendingData.amount}</Typography>
                      <Typography variant="caption" display="block"><strong>Date:</strong> {selectedItem.pendingData.date}</Typography>
                      <Typography variant="caption" display="block"><strong>Pay Mode:</strong> {selectedItem.pendingData.payMode}</Typography>
                      <Typography variant="caption" display="block"><strong>Daybook:</strong> {selectedItem.pendingData.dayBook}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                /* Static view for Deletion Requests */
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                    RECORD TO BE DELETED
                  </Typography>
                  <Typography variant="body2"><strong>Head/Category:</strong> {selectedItem.expenseHead || selectedItem.incomeHead || "-"}</Typography>
                  <Typography variant="body2"><strong>Recipient / Person:</strong> {selectedItem.personName}</Typography>
                  <Typography variant="body2"><strong>Amount:</strong> ₹{selectedItem.amount}</Typography>
                  <Typography variant="body2"><strong>Transaction Date:</strong> {selectedItem.date}</Typography>
                  <Typography variant="body2"><strong>Description:</strong> {selectedItem.description || "N/A"}</Typography>
                </Paper>
              )}

              {/* Admin Decision Note Input */}
              <TextField
                size="small"
                label="Admin Review Note / Reason"
                fullWidth
                multiline
                rows={2}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Optionally append notes to the audit trail..."
                inputProps={{ style: { fontSize: 13 } }}
                InputLabelProps={{ style: { fontSize: 13 } }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, justifyContent: 'space-between' }}>
          <Button onClick={() => setReviewModalOpen(false)} size="small" color="inherit" sx={{ fontSize: '12px' }}>
            Cancel
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={actionLoading}
              startIcon={<Cancel />}
              onClick={() => handleApprovalDecision(selectedItem, "reject")}
              sx={{ fontSize: '12px' }}
            >
              Reject Request
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              onClick={() => handleApprovalDecision(selectedItem, "approve")}
              sx={{ fontSize: '12px' }}
            >
              Approve Request
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApprovalManager;